import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cn } from '@/lib/cn';

/**
 * Ported from PulseOS (see HMS_UI_REDESIGN_ANALYSIS.md). Distinct from
 * TabBar.tsx, which is the pill-row page-section switcher already used by
 * AdminModule/PharmacyModule — this is full ARIA tablist semantics
 * (keyboard arrow navigation, roving tabindex) for tabs nested inside a
 * denser surface like a Dialog or a form section.
 */
const Tabs = TabsPrimitive.Root;

/**
 * Two looks, same ARIA behaviour:
 *   `pill`   (default) — a filled track with raised active pill; unchanged for
 *            every existing caller.
 *   `folder` — the claims wireframes' document tabs
 *            (docs/design/insurance-claims/Claims_Desk_Screens_Wireframe.html
 *            `.tabs`): separate bordered tabs with a rounded top, sitting on a
 *            2px rule; the active tab is white, bold and accent-bordered.
 * Pass the same variant to TabsList and its TabsTriggers.
 */
type TabsVariant = 'pill' | 'folder';

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & { variant?: TabsVariant }
>(({ className, variant = 'pill', ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      variant === 'folder'
        ? 'flex flex-wrap items-end gap-0.5 border-b-2 border-border'
        : 'inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground',
      className
    )}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & { variant?: TabsVariant }
>(({ className, variant = 'pill', ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      variant === 'folder'
        // `rounded-t-[5px] rounded-b-none` is not redundant: index.css gives
        // every bare `button` a full border-radius, which would round the
        // bottom corners off the rule the tab sits on.
        ? 'inline-flex items-center whitespace-nowrap rounded-t-[5px] rounded-b-none border border-b-0 border-border bg-muted ' +
          'px-3 py-[5px] text-[12.5px] font-normal text-ink transition-colors hover:bg-card ' +
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ' +
          'disabled:pointer-events-none disabled:opacity-50 ' +
          'data-[state=active]:border-teal data-[state=active]:bg-card data-[state=active]:font-bold data-[state=active]:text-teal'
        : 'inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium transition-all ' +
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ' +
          'disabled:pointer-events-none disabled:opacity-50 ' +
          'data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-sm',
      className
    )}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn('mt-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2', className)}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
